const defaultRespond = text => `You said: "${text || 'nothing'}". Replace this demo adapter with your own API call.`;

/**
 * A minimal in-memory `ChatAdapter` that echoes the user's last message.
 *
 * Intended for quickstart demos, prototyping, and tests — not for production.
 * The reply is emitted as a single text chunk after `delayMs` (no simulated
 * token-by-token streaming).
 */
export function createEchoAdapter(options = {}) {
  const respond = options.respond ?? defaultRespond;
  const delayMs = options.delayMs ?? 400;
  // Per-anchor attempt counter so a second regeneration of the same message
  // never collides with the reply id of the first.
  const attemptByMessageId = new Map();
  function createReplyStream(text, replyId, signal) {
    const reply = respond(text);
    const partId = `${replyId}-text`;
    let closed = false;
    let timer = null;
    let streamController = null;
    let handleAbort = () => {};
    const cleanup = () => {
      if (timer != null) {
        clearTimeout(timer);
        timer = null;
      }
      signal.removeEventListener('abort', handleAbort);
    };
    const close = () => {
      if (closed) {
        return;
      }
      closed = true;
      cleanup();
      try {
        streamController?.close();
      } catch {
        // already closed
      }
    };
    handleAbort = () => close();
    return new ReadableStream({
      start(controller) {
        streamController = controller;
        if (signal.aborted) {
          close();
          return;
        }
        timer = setTimeout(() => {
          timer = null;
          if (signal.aborted) {
            close();
            return;
          }
          closed = true;
          cleanup();
          controller.enqueue({
            type: 'start',
            messageId: replyId
          });
          controller.enqueue({
            type: 'text-start',
            id: partId
          });
          controller.enqueue({
            type: 'text-delta',
            id: partId,
            delta: reply
          });
          controller.enqueue({
            type: 'text-end',
            id: partId
          });
          controller.enqueue({
            type: 'finish',
            messageId: replyId,
            finishReason: 'stop'
          });
          controller.close();
        }, delayMs);
        signal.addEventListener('abort', handleAbort, {
          once: true
        });
      },
      cancel() {
        closed = true;
        cleanup();
        streamController = null;
      }
    });
  }
  const getMessageText = message => message.parts.map(part => part.type === 'text' ? part.text : '').join('');
  return {
    async sendMessage({
      message,
      signal
    }) {
      const text = getMessageText(message);
      return createReplyStream(text, `reply-${message.id}`, signal);
    },
    async regenerate({
      message,
      signal
    }) {
      const text = getMessageText(message);
      const attempt = (attemptByMessageId.get(message.id) ?? 0) + 1;
      attemptByMessageId.set(message.id, attempt);
      return createReplyStream(text, `reply-${message.id}-${attempt}`, signal);
    }
  };
}